import React from 'react';
import { AbsoluteFill, interpolate, useVideoConfig } from 'remotion';
import type { TransitionPresentation, TransitionPresentationComponentProps } from '@remotion/transitions';
import { HERO_ACCENT } from '../schema';

export type ThreadWipeProps = {
  /** Lean of the wipe edge in degrees (0 = vertical edge). */
  angleDeg?: number;
};

/**
 * Custom TransitionSeries presentation: the glowing demand-thread edge sweeps left → right and reveals the incoming
 * clip behind it. The outgoing clip is left untouched (no crossfade — hard reveal along the edge).
 */
const ThreadWipePresentation: React.FC<TransitionPresentationComponentProps<ThreadWipeProps>> = ({
  children,
  presentationDirection,
  presentationProgress,
  passedProps,
}) => {
  const { width, height } = useVideoConfig();

  if (presentationDirection === 'exiting') {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  const angle = passedProps.angleDeg ?? 14;
  const dx = Math.tan((angle * Math.PI) / 180) * height;
  const xTop = interpolate(presentationProgress, [0, 1], [-4, width + dx + 4]);
  const xBottom = xTop - dx;
  const clipPath = `polygon(0px 0px, ${xTop.toFixed(2)}px 0px, ${xBottom.toFixed(2)}px ${height}px, 0px ${height}px)`;
  const edgeOpacity = interpolate(presentationProgress, [0, 0.08, 0.92, 1], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill>
      <AbsoluteFill style={{ clipPath }}>{children}</AbsoluteFill>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
      >
        <defs>
          <filter id="hero-wipe-glow" x="-50%" y="-10%" width="200%" height="120%">
            <feGaussianBlur stdDeviation="12" />
          </filter>
        </defs>
        <g opacity={edgeOpacity}>
          <line
            x1={xTop}
            y1={0}
            x2={xBottom}
            y2={height}
            stroke={HERO_ACCENT}
            strokeWidth={34}
            opacity={0.55}
            filter="url(#hero-wipe-glow)"
          />
          <line x1={xTop} y1={0} x2={xBottom} y2={height} stroke={HERO_ACCENT} strokeWidth={6} />
          <line x1={xTop} y1={0} x2={xBottom} y2={height} stroke="#FFFFFF" strokeWidth={1.8} opacity={0.75} />
        </g>
      </svg>
    </AbsoluteFill>
  );
};

export const threadWipe = (props: ThreadWipeProps = {}): TransitionPresentation<ThreadWipeProps> => ({
  component: ThreadWipePresentation,
  props,
});

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
